<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const inboxName = ref('');
const isCreating = ref(false);

const createInbox = async () => {
  if (!inboxName.value.trim()) {
    useAlert(t('QUEPASA_INBOX.ERROR.NAME_REQUIRED'));
    return;
  }

  isCreating.value = true;

  try {
    // IMPORTANT: use the same Vuex flow as core channels (ex: API.vue)
    // so the inbox record is committed to the store immediately.
    // This ensures Settings.vue can render the correct provider-specific tabs.
    const createdInbox = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value,
      channel: {
        type: 'quepasa',
      },
    });

    useAlert(t('QUEPASA_INBOX.SUCCESS.INBOX_CREATED'));

    // Navigate to agents setup
    router.push({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: createdInbox.id,
      },
    });
  } catch (error) {
    useAlert(t('QUEPASA_INBOX.ERROR.CREATION_FAILED'));
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="flex h-full flex-col overflow-auto">
    <div class="mx-auto w-full max-w-lg p-6">
      <div class="mb-6">
        <h2
          class="mb-2 text-2xl font-semibold text-slate-900 dark:text-slate-25"
        >
          {{ $t('QUEPASA_INBOX.TITLE') }}
        </h2>
        <p class="text-sm text-slate-700 dark:text-slate-100">
          {{ $t('QUEPASA_INBOX.DESCRIPTION') }}
        </p>
      </div>

      <form class="space-y-4" @submit.prevent="createInbox">
        <div>
          <label
            for="inbox-name"
            class="mb-2 block text-sm font-medium text-slate-900 dark:text-slate-25"
          >
            {{ $t('QUEPASA_INBOX.INBOX.NAME.LABEL') }}
          </label>
          <input
            id="inbox-name"
            v-model="inboxName"
            type="text"
            :placeholder="$t('QUEPASA_INBOX.INBOX.NAME.PLACEHOLDER')"
            class="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-woot-500 focus:outline-none focus:ring-1 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-25"
            autocomplete="off"
            :disabled="isCreating"
            required
          />
          <p class="mt-1 text-xs text-slate-600 dark:text-slate-200">
            {{ $t('QUEPASA_INBOX.INBOX.NAME.HELP') }}
          </p>
        </div>

        <div class="flex gap-2 pt-4">
          <button
            type="submit"
            :disabled="isCreating || !inboxName.trim()"
            class="flex-1 rounded-lg bg-woot-500 px-4 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span v-if="!isCreating">
              {{ $t('QUEPASA_INBOX.INBOX.CREATE.SUBMIT') }}
            </span>
            <span v-else class="flex items-center justify-center gap-2">
              <i class="i-lucide-loader-2 h-4 w-4 animate-spin" />
              {{ $t('QUEPASA_INBOX.INBOX.CREATE.CREATING') }}
            </span>
          </button>
          <button
            type="button"
            :disabled="isCreating"
            class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800"
            @click="router.go(-1)"
          >
            {{ $t('QUEPASA_INBOX.INBOX.CREATE.CANCEL') }}
          </button>
        </div>
      </form>

      <div
        class="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20"
      >
        <div class="flex gap-3">
          <i class="i-lucide-info h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div class="flex-1">
            <h4
              class="mb-1 text-sm font-medium text-blue-900 dark:text-blue-100"
            >
              {{ $t('QUEPASA_INBOX.INBOX.CREATE.INFO_TITLE') }}
            </h4>
            <p class="text-xs text-blue-800 dark:text-blue-200">
              {{ $t('QUEPASA_INBOX.INBOX.CREATE.INFO_DESCRIPTION') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
